<template>
  <div class="home">
    <h1>🎄 Świąteczne Wyzwanie 2025 🎄</h1>
    <h2>
      Wykonane zadania: {{ completedPuzzles.length }} / {{ tasks.length }}
    </h2>
    <div class="progress-bar">
      <div
        class="progress-fill"
        :style="{ width: (completedPuzzles.length / tasks.length) * 100 + '%' }"
      ></div>
    </div>

    <div
      v-if="completedPuzzles.length === tasks.length"
      class="congrats-container"
    >
      <router-link to="/congrats" class="congrats-button">
        <span class="button-text">Odbierz nagrodę!</span>
        <span class="button-sparkles">
          <span class="sparkle">✨</span>
          <span class="sparkle">✨</span>
          <span class="sparkle">✨</span>
        </span>
      </router-link>
    </div>

    <div class="puzzle-grid challenge-container">
      <router-link
        v-for="(task, index) in tasks"
        :key="index"
        :to="task.to"
        class="puzzle-panel"
        :class="{ completed: isPuzzleCompleted(task.id) }"
      >
        <p>{{ task.label }}</p>
      </router-link>
    </div>

    <!-- Sekcja debug -->
    <div class="debug-section">
      <button class="debug-toggle" @click="toggleDebug">
        {{ showDebug ? "Ukryj debug" : "Pokaż debug" }}
      </button>
      <div v-if="showDebug" class="debug-buttons">
        <button
          v-for="(task, index) in tasks"
          :key="index"
          @click="completeTask(task.id)"
        >
          Ukończ {{ task.label }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";

const tasks = [
  { id: "puzzle1", label: "Quiz o programowaniu", to: "/quiz" },
  { id: "puzzle2", label: "Odszyfruj wiadomość", to: "/decrypt" },
  { id: "puzzle3", label: "Napraw lampki", to: "/fix-lights" },
  { id: "puzzle4", label: "Przygoda tekstowa", to: "/adventure" },
];

const completedPuzzles = ref([]);
const showDebug = ref(false);

onMounted(() => {
  const cookies = document.cookie.split("; ");
  completedPuzzles.value = cookies
    .filter((cookie) => cookie.startsWith("puzzle"))
    .map((cookie) => cookie.split("=")[0]);
});

const isPuzzleCompleted = (puzzle) => {
  return completedPuzzles.value.includes(puzzle);
};

const completeTask = (taskId) => {
  document.cookie = `${taskId}=completed; max-age=3600`;
  completedPuzzles.value = [...completedPuzzles.value, taskId];
};

const toggleDebug = () => {
  showDebug.value = !showDebug.value;
};
</script>

<style scoped>
/* Główne style */
h1 {
  color: #d32f2f;
  text-align: center;
}

.puzzle-grid {
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 24px;
  max-width: 600px;
  margin: 0 auto;
}

.puzzle-panel {
  background-color: #f9fbe7;
  border: 2px solid #d32f2f;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  text-decoration: none;
  color: #d32f2f;
  font-size: 1.4em;
  font-family: "Roboto", sans-serif;
  transition: transform 0.3s, background-color 0.3s;
  position: relative;
  overflow: hidden;
}

.puzzle-panel:hover {
  background-color: #ebebeb;
}

.puzzle-panel.completed {
  background-color: #c8e6c9;
  border-color: #388e3c;
  color: #1b5e20;
}

.puzzle-panel.completed::after {
  content: "✓";
  position: absolute;
  top: 12px;
  right: 12px;
  font-size: 1.6em;
  color: #388e3c;
}

.progress-bar {
  width: 100%;
  height: 20px;
  background-color: #e0e0e0;
  border-radius: 10px;
  overflow: hidden;
  margin: 16px auto;
  max-width: 600px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #64b5f6, #4caf50);
  transition: width 0.5s;
}

/* Kontener z gratulacjami */
.congrats-container {
  text-align: center;
  margin: 30px 0;
}

.congrats-text {
  font-size: 1.5em;
  color: #d32f2f;
  margin-bottom: 20px;
  font-weight: bold;
}

/* Guzik z gratulacjami */
.congrats-button {
  display: inline-block;
  position: relative;
  background: linear-gradient(135deg, #d32f2f, #b71c1c);
  color: white;
  text-decoration: none;
  padding: 16px 32px;
  border-radius: 50px;
  font-size: 1.4em;
  font-weight: bold;
  text-align: center;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  transition: all 0.3s;
  overflow: hidden;
  border: 2px solid #ffd700;
  animation: pulse 2s infinite;
}

.congrats-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}

.button-text {
  position: relative;
  z-index: 2;
}

.button-sparkles {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.sparkle {
  position: absolute;
  color: #ffd700;
  font-size: 1.5em;
  opacity: 0;
  animation: sparkle 2s infinite;
}

.sparkle:nth-child(1) {
  top: 10%;
  left: 20%;
  animation-delay: 0s;
}

.sparkle:nth-child(2) {
  top: 60%;
  left: 50%;
  animation-delay: 0.5s;
}

.sparkle:nth-child(3) {
  top: 30%;
  left: 80%;
  animation-delay: 1s;
}

/* Animacje */
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(211, 47, 47, 0.7);
  }
  70% {
    box-shadow: 0 0 0 15px rgba(211, 47, 47, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(211, 47, 47, 0);
  }
}

@keyframes sparkle {
  0% {
    opacity: 0;
    transform: scale(0.5);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
  100% {
    opacity: 0;
    transform: scale(0.8);
  }
}

/* Sekcja debug */
.debug-section {
  margin-top: 30px;
  text-align: center;
}

.debug-toggle {
  background-color: #9e9e9e;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  margin-bottom: 10px;
}

.debug-toggle:hover {
  background-color: #757575;
}

.debug-buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
}

.debug-buttons button {
  background-color: #64b5f6;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  width: 100%;
}

.debug-buttons button:hover {
  background-color: #42a5f5;
}
</style>
